import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Col, Row } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { GET_DASHBOARD_V2_PERFORMANCE } from 'actions/dashboardV2';
import { ObjectId } from 'bson';
import moment from 'moment';
import Card from 'components/inputs/Card';
import ChartEmbed from './ChartEmbed';
import PerformanceMetrics from './PerformanceMetrics';
import StationTables from './StationTables';
import PeriodFilter from './PeriodFilter';
import { RANGE_LABEL_STYLE, formatRange, getDateRange, getRangeLabel } from './dateRanges';
import { CHART_IDS } from '../chartConstants';
import { useWidgetPermissions, V2_WIDGET_KEYS } from './widgetPermissions';

const V2_CHART_IDS = {
  sessions: CHART_IDS.V2_SESSIONS,
  units: CHART_IDS.V2_UNITS,
  energyWeekly: CHART_IDS.V2_ENERGY_WEEKLY,
  energyMonthly: CHART_IDS.V2_ENERGY_MONTHLY,
  activeUsers: CHART_IDS.V2_ACTIVE_USERS,
  newUsers: CHART_IDS.V2_NEW_USERS,
};

const KPI_HEIGHT = '120px';
const CHART_HEIGHT = '260px';

// /dashboard/v2/performance only returns these fixed buckets; other periods need BE from/to support.
// NOTE: 'last_week' is served by the API's lastWeek bucket as an interim measure — if that bucket is
// a rolling 7 days rather than the previous calendar week, the figure won't match the grey range.
const PERIOD_TO_REVENUE_KEY = {
  today: 'today',
  yesterday: 'yesterday',
  last_week: 'lastWeek',
  '7d': 'lastWeek',
  '30d': 'last30d',
};

const formatRevenue = (value) => `₹${Number(value).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

// Fixed two-line header: keeps every KPI card the same height regardless of title length.
const KpiHeader = ({ title, rangeLabel }) => (
  <div style={{ marginBottom: 8 }}>
    <div title={title} style={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: '20px' }}>
      {title}
    </div>
    <div style={{ ...RANGE_LABEL_STYLE, lineHeight: '16px' }}>{rangeLabel}</div>
  </div>
);

KpiHeader.propTypes = {
  title: PropTypes.string.isRequired,
  rangeLabel: PropTypes.string.isRequired,
};

const last12Months = () => ({
  from: moment().subtract(11, 'months').startOf('month').toDate(),
  to: moment().endOf('month').toDate(),
});

const last12Weeks = () => ({
  from: moment().subtract(11, 'weeks').startOf('isoWeek').toDate(),
  to: moment().endOf('isoWeek').toDate(),
});

const DashboardV2Section = () => {
  const dispatch = useDispatch();
  const profileData = useSelector((state) => state.profile.userProfile);
  const performance = useSelector((state) => state.dashboardV2?.performance);
  const tenantId = profileData?.tenant?.id;

  // CDB-494 — each widget is a child module under Dashboardv2, toggled per role in
  // Configure Role and read back through isAllowed(), exactly as the v1 widgets are.
  const canSeeWidget = useWidgetPermissions();
  const showRevenue = canSeeWidget(V2_WIDGET_KEYS.revenue);
  const showEnergyWeekly = canSeeWidget(V2_WIDGET_KEYS.energySoldWeekly);
  const showEnergyMonthly = canSeeWidget(V2_WIDGET_KEYS.energySoldMonthly);
  const showPerformance =
    canSeeWidget(V2_WIDGET_KEYS.avgRevenuePerSession) ||
    canSeeWidget(V2_WIDGET_KEYS.avgSessionDuration) ||
    canSeeWidget(V2_WIDGET_KEYS.peakUsageHours);

  const showStations =
    canSeeWidget(V2_WIDGET_KEYS.topStationsRevenue) ||
    canSeeWidget(V2_WIDGET_KEYS.topStationsUtilization) ||
    canSeeWidget(V2_WIDGET_KEYS.worstStationsRevenue) ||
    canSeeWidget(V2_WIDGET_KEYS.worstStationsUtilization);

  // Revenue and the three performance cards are all served by /dashboard/v2/performance,
  // so the call is skipped entirely when none of them is visible to this role.
  const needsPerformanceData = showRevenue || showPerformance;

  useEffect(() => {
    if (needsPerformanceData) {
      dispatch({ type: GET_DASHBOARD_V2_PERFORMANCE.REQUEST });
    }
  }, [dispatch, needsPerformanceData]);

  // One shared filter drives Revenue / Sessions / Units / Active Users / New Users.
  const [period, setPeriod] = useState('today');

  const kpiRange = getDateRange(period);
  const weekRange = last12Weeks();
  const yearRange = last12Months();

  const tenantFilter = tenantId ? { tenant: { $eq: new ObjectId(tenantId) } } : {};

  const buildFilter = ({ from, to }) => ({
    ...tenantFilter,
    booking_stop: { $gte: from, $lte: to },
  });

  const userFilter = ({ from, to }) => ({
    ...tenantFilter,
    createdAt: { $gte: from, $lte: to },
  });

  const KPI_WIDGETS = [
    {
      key: 'sessions',
      title: 'Sessions',
      chartId: V2_CHART_IDS.sessions,
      filter: buildFilter(kpiRange),
      permission: V2_WIDGET_KEYS.sessions,
    },
    {
      key: 'units',
      title: 'Units Consumed (kWh)',
      chartId: V2_CHART_IDS.units,
      filter: buildFilter(kpiRange),
      permission: V2_WIDGET_KEYS.unitsConsumed,
    },
    {
      key: 'activeUsers',
      title: 'Active Users',
      chartId: V2_CHART_IDS.activeUsers,
      filter: buildFilter(kpiRange),
      permission: V2_WIDGET_KEYS.activeUsers,
    },
    // Filtered on signup date, so the shared range applies to createdAt rather than booking_stop.
    {
      key: 'newUsers',
      title: 'New Users',
      chartId: V2_CHART_IDS.newUsers,
      filter: userFilter(kpiRange),
      permission: V2_WIDGET_KEYS.newUsers,
    },
  ].filter(({ permission }) => canSeeWidget(permission));

  // Tiles share the row evenly. Bootstrap has no 5-column unit, so the row uses flex
  // basis, and the basis follows how many tiles this role can actually see — otherwise
  // hiding a widget would leave a gap where it used to be.
  const visibleTileCount = (showRevenue ? 1 : 0) + KPI_WIDGETS.length;
  const tileBasis = visibleTileCount > 0 ? `${100 / visibleTileCount}%` : '100%';
  const TILE_STYLE = { flex: `1 1 ${tileBasis}`, maxWidth: tileBasis, marginBottom: 12, display: 'flex' };

  // A role with none of the Dashboardv2 permissions sees no section at all — not an
  // empty shell with a period filter and nothing under it.
  const hasAnyWidget = showRevenue || KPI_WIDGETS.length > 0 || showEnergyWeekly || showEnergyMonthly || showPerformance || showStations;

  const kpiRangeLabel = getRangeLabel(period);
  const revenueKey = PERIOD_TO_REVENUE_KEY[period];
  const revenueValue = revenueKey ? performance?.[revenueKey]?.totalRevenue : null;

  if (!hasAnyWidget) {
    return null;
  }

  // The chart embeds are filtered by tenant on the client, so rendering before the profile
  // has loaded would send them with no tenant clause at all — every tenant's data, for as
  // long as the profile takes to arrive. Previously the caller's `dashboardList` check hid
  // this by accident; CDB-1954 removed that dependency, so guard it here where the filter
  // is actually built.
  if (!tenantId) {
    return null;
  }

  return (
    <div className="dashboard-v2-section">
      {/* CDB-1957 — the period filter drives only the five KPI tiles, so it goes away with them.
          Left on its own it is a control that changes nothing: the energy charts run on fixed
          12-week/12-month ranges, the performance cards on fixed buckets, and the station tables
          have their own period buttons. */}
      {visibleTileCount > 0 && (
        <>
          {/* Shared KPI filter — Revenue / Sessions / Units / Active Users / New Users */}
          <PeriodFilter title="Analytics" period={period} onPeriodChange={setPeriod} rangeLabel={kpiRangeLabel} />

          {/* Row 1 — KPI stat widgets */}
          <Row className="d-flex align-items-stretch">
            {showRevenue && (
              <Col style={TILE_STYLE}>
                <Card className="h-100 w-100">
                  <KpiHeader title="Revenue" rangeLabel={kpiRangeLabel} />
                  <div
                    style={{
                      height: KPI_HEIGHT,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 28,
                      fontWeight: 700,
                    }}
                  >
                    {revenueValue != null ? formatRevenue(revenueValue) : '—'}
                  </div>
                </Card>
              </Col>
            )}
            {KPI_WIDGETS.map(({ key, title, chartId, filter }) => (
              <Col key={key} style={TILE_STYLE}>
                <Card className="h-100 w-100">
                  <KpiHeader title={title} rangeLabel={kpiRangeLabel} />
                  <ChartEmbed chartId={chartId} height={KPI_HEIGHT} filter={filter} />
                </Card>
              </Col>
            ))}
          </Row>
        </>
      )}

      {/* Row 2 — Energy Sold weekly/monthly. A single visible chart takes the full width. */}
      {(showEnergyWeekly || showEnergyMonthly) && (
        <Row className="d-flex align-items-stretch">
          {showEnergyWeekly && (
            <Col md={showEnergyMonthly ? 6 : 12} className="d-flex" style={{ marginBottom: 12 }}>
              <Card className="h-100 w-100">
                <KpiHeader title="Energy Sold — Weekly" rangeLabel={formatRange(weekRange.from, weekRange.to)} />
                <ChartEmbed chartId={V2_CHART_IDS.energyWeekly} height={CHART_HEIGHT} filter={buildFilter(weekRange)} />
              </Card>
            </Col>
          )}
          {showEnergyMonthly && (
            <Col md={showEnergyWeekly ? 6 : 12} className="d-flex" style={{ marginBottom: 12 }}>
              <Card className="h-100 w-100">
                <KpiHeader title="Energy Sold — Monthly" rangeLabel={formatRange(yearRange.from, yearRange.to)} />
                <ChartEmbed chartId={V2_CHART_IDS.energyMonthly} height={CHART_HEIGHT} filter={buildFilter(yearRange)} />
              </Card>
            </Col>
          )}
        </Row>
      )}

      {/* Row 3 — Performance Metrics (BE API) */}
      {showPerformance && (
        <>
          <div style={{ fontWeight: 600, marginBottom: 12 }}>Performance Metrics</div>
          <Row className="d-flex align-items-stretch">
            <PerformanceMetrics />
          </Row>
        </>
      )}

      {/* Row 4 — Station Tables (BE API) */}
      {showStations && <StationTables />}
    </div>
  );
};

export default DashboardV2Section;
